import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";

const PHONE_VERIFICATION_TIMEOUT_MS = 90_000;
const SESSION_STEP_TIMEOUT_MS = 20_000;

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

const withTimeout = (promise, stage, message, timeoutMs = SESSION_STEP_TIMEOUT_MS) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(withAuthStage(createAuthError("auth/operation-timeout", message), stage));
  }, timeoutMs);
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timeoutId); resolve(value); },
    (error) => { clearTimeout(timeoutId); reject(withAuthStage(error, stage)); },
  );
});

export const isNativeAndroidAuth = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

export const isNativeIosAuth = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

const createAuthError = (code, message, cause) => {
  const error = new Error(message);
  error.code = code;
  error.cause = cause;
  return error;
};

const withAuthStage = (error, stage) => {
  const authError = error instanceof Error
    ? error
    : createAuthError(error?.code || "auth/unknown", error?.message || "Unknown authentication error", error);
  try {
    if (!authError.authStage) authError.authStage = stage;
  } catch {
    return createAuthError(authError.code || "auth/unknown", authError.message, authError);
  }
  return authError;
};

const safeSessionState = (webAuth) => ({
  hasCurrentUser: Boolean(webAuth?.currentUser),
  uidSuffix: webAuth?.currentUser?.uid?.slice(-6) || null,
});

const sanitizeAuthLogText = (value, fallback) => {
  const text = String(value || fallback || "")
    .replace(/AIza[0-9A-Za-z_-]{20,}/g, "[REDACTED_API_KEY]")
    .replace(/eyJ[0-9A-Za-z_-]+\.[0-9A-Za-z_-]+\.[0-9A-Za-z_-]+/g, "[REDACTED_JWT]")
    .replace(/([?&](?:access_token|id_token|code|token|key|secret)=)[^&\s]+/gi, "$1[REDACTED]")
    .replace(/\+?55\s*\(?\d{2}\)?[\s-]*\d{4,5}[\s-]*\d{4}/g, "[REDACTED_PHONE]");
  return text.slice(0, 600);
};

export const logAuthCheckpoint = (stage, details = {}) => {
  // Never log credentials, phone numbers, SMS codes, tokens or OAuth parameters.
  const payload = { stage, platform: Capacitor.getPlatform(), ...details };
  console.info(`[ZunoAuth] ${JSON.stringify(payload)}`);
};

export const logAuthError = (flow, error, context = {}) => {
  // Never log credentials, phone numbers, SMS codes, tokens or OAuth parameters.
  const payload = {
    flow,
    stage: error?.authStage || context.stage || "unknown",
    provider: context.provider || null,
    platform: Capacitor.getPlatform(),
    code: sanitizeAuthLogText(error?.code, "auth/unknown"),
    name: sanitizeAuthLogText(error?.name, "Error"),
    message: sanitizeAuthLogText(error?.message, "Unknown authentication error"),
    ...safeSessionState(context.webAuth),
  };
  console.error(`[ZunoAuth] ${JSON.stringify(payload)}`);
};

const waitForPersistence = async (persistenceReady, provider) => {
  if (!persistenceReady) return;
  try {
    const persistence = await withTimeout(
      persistenceReady,
      `${provider}/persistence`,
      "Firebase persistence initialization timed out.",
      2500,
    );
    logAuthCheckpoint(`${provider}/persistence-ready`, {
      persistenceMode: persistence?.mode || "default",
      persistenceReady: persistence?.ok !== false,
    });
  } catch (error) {
    // Persistência é uma otimização; nunca deve impedir o provedor de abrir.
    logAuthCheckpoint(`${provider}/persistence-bypassed`, {
      code: error?.code || "auth/persistence-timeout",
    });
  }
};

const validateWebSession = async (webAuth, result, stage, provider) => {
  if (!result?.user) {
    throw createAuthError("auth/web-session-missing-user", "Firebase Web authentication returned no user.");
  }
  try {
    const idToken = await withTimeout(
      result.user.getIdToken(),
      `${provider}/id-token`,
      "Firebase Web session token timed out.",
    );
    if (!idToken) throw createAuthError("auth/web-session-missing-token", "Firebase Web authentication returned no session token.");
    if (typeof webAuth.authStateReady === "function") {
      await withTimeout(
        webAuth.authStateReady(),
        `${provider}/auth-state`,
        "Firebase Web authentication state timed out.",
      );
    }
    if (webAuth.currentUser?.uid !== result.user.uid) {
      throw createAuthError("auth/web-session-not-persisted", "Firebase Web session was not committed to the current Auth instance.");
    }
    logAuthCheckpoint(`${provider}/web-session-ready`, {
      hasCurrentUser: true,
      uidSuffix: result.user.uid.slice(-6),
    });
    logAuthCheckpoint("FIREBASE_CURRENT_USER_OK", { provider });
    return result;
  } catch (error) {
    throw withAuthStage(error, stage);
  }
};

const signInWebWithNativeCredential = async (webAuth, credential, provider) => {
  const startedAt = now();
  try {
    logAuthCheckpoint(`${provider}/firebase-sign-in-called`, {
      hasCredential: Boolean(credential),
    });
    logAuthCheckpoint("FIREBASE_SIGNIN_STARTED", { provider });
    const webResult = await withTimeout(
      signInWithCredential(webAuth, credential),
      `${provider}/firebase-sign-in`,
      "Firebase Web sign-in timed out.",
    );
    logAuthCheckpoint(`${provider}/firebase-user-obtained`, {
      hasUser: Boolean(webResult?.user),
      uidSuffix: webResult?.user?.uid?.slice(-6) || null,
      elapsedMs: Math.round(now() - startedAt),
    });
    logAuthCheckpoint("FIREBASE_SIGNIN_SUCCESS", {
      provider,
      elapsedMs: Math.round(now() - startedAt),
    });
    return await validateWebSession(webAuth, webResult, `${provider}/web-session`, provider);
  } catch (error) {
    throw withAuthStage(error, `${provider}/firebase-sign-in`);
  }
};

const createErrorForMissingCredential = (provider) => createAuthError(
  "auth/native-credential-missing",
  `${provider} sign-in returned no usable credential.`,
);

export const getAuthErrorMessage = (error, flow = "login") => {
  const signature = `${error?.code || ""} ${error?.message || ""}`.toLowerCase();

  if (signature.includes("popup-closed") || signature.includes("canceled") || signature.includes("cancelled")) return "Login cancelado. Tente novamente quando quiser.";
  if (signature.includes("popup-blocked")) return "Pop-up bloqueado. Permita pop-ups neste site.";
  if (signature.includes("unauthorized-domain")) return "Domínio não autorizado. Contate o suporte.";
  if (signature.includes("requests-from-referer") || (signature.includes("referer") && signature.includes("blocked"))) return "Não foi possível concluir o login. Tente novamente.";
  if (signature.includes("operation-not-allowed")) return "Este login ainda precisa ser habilitado no Firebase. Contate o suporte.";
  if (signature.includes("invalid-phone-number")) return "Número inválido. Confira o DDD e tente novamente.";
  if (signature.includes("too-many-requests")) return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
  if (signature.includes("quota-exceeded")) return "Limite de SMS temporariamente atingido. Tente novamente mais tarde.";
  if (signature.includes("invalid-verification-code")) return "Código incorreto. Confira o SMS e tente novamente.";
  if (signature.includes("code-expired") || signature.includes("session-expired")) return "Código expirado. Solicite um novo.";
  if (signature.includes("network-request-failed") || signature.includes("network error")) return "Sem conexão com o serviço de autenticação. Verifique sua internet.";
  if (signature.includes("operation-timeout") || signature.includes("timed out")) return "A autenticação demorou demais. Verifique sua internet e tente novamente.";
  if (signature.includes("invalid-app-credential") || signature.includes("app-not-authorized") || signature.includes("developer_error") || signature.includes("configuration") || signature.includes("missing-google-id-token")) {
    return "A autenticação deste aplicativo precisa ser configurada. Contate o suporte.";
  }

  if (flow === "sms") return "Não foi possível enviar o SMS. Tente novamente.";
  if (flow === "code") return "Não foi possível verificar o código. Tente novamente.";
  return "Não foi possível entrar. Tente novamente.";
};

export async function signInWithNativeGoogle(webAuth, _functionsClient, persistenceReady) {
  await waitForPersistence(persistenceReady, "google");
  logAuthCheckpoint("GOOGLE_START");
  logAuthCheckpoint("GOOGLE_PROVIDER_OPENED");
  let result;
  try {
    result = await FirebaseAuthentication.signInWithGoogle({
      skipNativeAuth: true,
      useCredentialManager: true,
    });
  } catch (error) {
    throw withAuthStage(error, "google/native-provider");
  }
  const idToken = result?.credential?.idToken;
  if (!idToken) throw createErrorForMissingCredential("google");
  logAuthCheckpoint("GOOGLE_ACCOUNT_SELECTED");
  logAuthCheckpoint("GOOGLE_CALLBACK_RETURNED");
  logAuthCheckpoint("GOOGLE_ID_TOKEN_RECEIVED");
  logAuthCheckpoint("google/native-credential-ready", { hasIdToken: true });
  const credential = GoogleAuthProvider.credential(idToken, result?.credential?.accessToken || undefined);
  logAuthCheckpoint("FIREBASE_CREDENTIAL_CREATED", { provider: "google" });
  logAuthCheckpoint("google/firebase-credential-created", { hasCredential: Boolean(credential) });
  return signInWebWithNativeCredential(webAuth, credential, "google");
}

export async function signInWithNativeApple(webAuth, _functionsClient, persistenceReady) {
  await waitForPersistence(persistenceReady, "apple");
  logAuthCheckpoint("APPLE_START");
  let result;
  try {
    result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
  } catch (error) {
    throw withAuthStage(error, "apple/native-provider");
  }
  const idToken = result?.credential?.idToken;
  const rawNonce = result?.credential?.nonce;
  if (!idToken || !rawNonce) throw createErrorForMissingCredential("apple");
  logAuthCheckpoint("APPLE_CALLBACK");
  logAuthCheckpoint("APPLE_ID_TOKEN");
  logAuthCheckpoint("APPLE_NONCE_VALID");
  logAuthCheckpoint("apple/native-credential-ready", { hasIdToken: true, hasNonce: true });
  const credential = new OAuthProvider("apple.com").credential({ idToken, rawNonce });
  logAuthCheckpoint("FIREBASE_CREDENTIAL", { provider: "apple" });
  logAuthCheckpoint("apple/firebase-credential-created", { hasCredential: Boolean(credential) });
  return signInWebWithNativeCredential(webAuth, credential, "apple");
}

export async function startNativePhoneVerification(phoneNumber) {
  let codeSentHandle;
  let failedHandle;
  let timeoutId;

  const cleanup = async () => {
    clearTimeout(timeoutId);
    await Promise.allSettled([codeSentHandle?.remove(), failedHandle?.remove()]);
  };

  return new Promise(async (resolve, reject) => {
    try {
      codeSentHandle = await FirebaseAuthentication.addListener("phoneCodeSent", async ({ verificationId }) => {
        await cleanup();
        if (!verificationId) {
          reject(createAuthError("auth/missing-verification-id", "Native phone auth returned no verification ID."));
          return;
        }
        logAuthCheckpoint("phone/native-code-sent", { hasVerificationId: true });
        logAuthCheckpoint("VERIFICATION_ID_RECEIVED");
        logAuthCheckpoint("SMS_SENT");
        resolve(verificationId);
      });
      failedHandle = await FirebaseAuthentication.addListener("phoneVerificationFailed", async (event) => {
        await cleanup();
        reject(createAuthError(event?.code || "auth/phone-verification-failed", event?.message || "Native phone verification failed."));
      });
      timeoutId = setTimeout(async () => {
        await cleanup();
        reject(createAuthError("auth/phone-verification-timeout", "Native phone verification timed out."));
      }, PHONE_VERIFICATION_TIMEOUT_MS);

      logAuthCheckpoint("PHONE_START");
      logAuthCheckpoint("APP_VERIFICATION_START");
      await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber, skipNativeAuth: false });
    } catch (error) {
      await cleanup();
      reject(withAuthStage(error, "phone/native-verification"));
    }
  });
}

export async function confirmNativePhoneCode(webAuth, _functionsClient, verificationId, verificationCode, persistenceReady) {
  if (!verificationId) throw createAuthError("auth/missing-verification-id", "No native phone verification ID is available.");
  if (!verificationCode || verificationCode.length !== 6) throw createAuthError("auth/invalid-verification-code", "The verification code must have six digits.");
  await waitForPersistence(persistenceReady, "phone");
  logAuthCheckpoint("phone/native-code-confirm", { hasVerificationId: true, codeLength: verificationCode.length });
  logAuthCheckpoint("SMS_CODE_CONFIRMED");
  try {
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    logAuthCheckpoint("phone/firebase-credential-created", { hasCredential: Boolean(credential) });
    return await signInWebWithNativeCredential(webAuth, credential, "phone");
  } catch (error) {
    throw withAuthStage(error, "phone/native-code-confirm");
  }
}
